import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { Tooltip } from '@chakra-ui/react';
import {
  IconButton,
  Button,
  useNexus,
  Drawer,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Tag as NexusTag,
  SelectMenu,
  showToast,
} from '@spotoninc/nexus-react';
import { DatePicker } from 'rsuite';
import {
  ChevronUpCircleIcon,
  UsersIcon,
  CalendarIcon,
  EditIcon,
} from '@spotoninc/nexus-web-assets';
import { fetchWithAuth } from '../../../utils/apiHelpers';

// Import SVG images
import completedSvg from '../../../media/images/completed-status.svg';
import cancelledSvg from '../../../media/images/cancelled-status.svg';
import inProgressSvg from '../../../media/images/in-progress.svg';
import todoSvg from '../../../media/images/todo.svg';
import statusSvg from '../../../media/images/status.svg';
import deleteIconSvg from '../../../media/images/delete-icon.svg';
import listNumberIconSvg from '../../../media/images/list-number-icon.svg';
import '../../../styles/reskinned-task-list.css';

// Helper functions moved outside component to prevent recreation on each render
// Function to get status image based on status
const getStatusImage = status => {
  switch (status) {
    case 'Completed':
      return completedSvg;
    case 'Cancelled':
      return cancelledSvg;
    case 'In Progress':
      return inProgressSvg;
    default:
      return todoSvg;
  }
};

// Function to extract priority from task description using regex
const extractPriorityFromDescription = description => {
  if (!description) return 'Medium'; // Default priority if no description

  // Regex to match "Priority: X" where X is the priority level
  const priorityMatch = description.match(/Priority:\s*(High|Medium|Low)/i);

  // Return the matched priority or default to Medium if not found
  return priorityMatch ? priorityMatch[1] : 'Medium';
};

// Function to determine tag variant based on priority
const getPriorityVariant = priority => {
  const priorityLower = priority.toLowerCase();

  if (priorityLower === 'high') return 'danger';
  if (priorityLower === 'medium') return 'warning';
  if (priorityLower === 'low') return 'info';

  return 'warning'; // Default to warning for unknown priorities
};

/**
 * Task Drawer component that can be used with any component
 * This component handles displaying task details in a drawer
 * It also handles all task updates internally
 *
 * @param {Object} selectedTask - The task to display in the drawer
 * @param {boolean} isDrawerOpen - Whether the drawer is open
 * @param {Function} onClose - Function to call when the drawer is closed
 * @param {Function} fetchTasks - Function to call to refresh tasks after updates
 * @param {boolean} isFromTaskTable - Whether the drawer is being rendered from the taskTable.jsx
 */
const TaskDrawer = ({
  selectedTask,
  isDrawerOpen,
  onClose,
  fetchTasks,
  isFromTaskTable = false,
}) => {
  const { theme } = useNexus();
  const [isClosing, setIsClosing] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isEditingAssignment, setIsEditingAssignment] = useState(false);
  const [isEditingDueDate, setIsEditingDueDate] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  // Create a local copy of the task that we can update
  const [localTask, setLocalTask] = useState(null);
  const assignmentRef = useRef(null);
  const dueDateRef = useRef(null);

  // Function to format task description with sections - copied from ReskinnedTaskList.jsx
  const formatTaskDescription = useCallback((description, header) => {
    if (!description) return '';

    // Check if this is a summary type task
    const isSummaryTask = header?.toLowerCase().includes('summary');
    // Check if this is an error type task
    const isErrorTask = header?.toLowerCase().includes('error');

    // Define the sections we want to identify
    const summaryTaskSections = [
      'KPIs:',
      'What went well this period:',
      'What can be improved for future periods:',
      'Task 1',
      'Task 2',
      'Task 3',
    ];

    // Define subsections within tasks
    const taskSubsections = [
      'Data Points:',
      'Solution:',
      'Potential Impact:',
      'Priority:',
    ];

    const errorTaskSections = ['Data Points:', 'Potential Impact:'];

    // Helper function to render content as bullet points
    const renderAsBulletList = (sectionTitle, sectionContent) => {
      // Split the content by sentences while ignoring decimal points in numbers
      const sentences = [];
      let currentSentence = '';
      const words = sectionContent.trim().split(/\s+/);

      for (let i = 0; i < words.length; i++) {
        const word = words[i];
        currentSentence += (currentSentence ? ' ' : '') + word;

        // Check if this word ends a sentence
        const isNumberWithDecimal = /\d+\.\d+/.test(word);
        const isPercentageOrMoney = /\$?\d+\.\d+%?/.test(word);
        const isCommonAbbreviation =
          /^(Mr\.|Mrs\.|Dr\.|Inc\.|Ltd\.|etc\.)$/.test(word);

        if (
          word.endsWith('.') &&
          !isNumberWithDecimal &&
          !isPercentageOrMoney &&
          !isCommonAbbreviation
        ) {
          sentences.push(currentSentence.trim());
          currentSentence = '';
        }
      }

      // Add any remaining text as the last sentence
      if (currentSentence.trim()) {
        sentences.push(currentSentence.trim());
      }

      // Check if this is a special section that needs custom styling
      const sectionTrimmed = sectionTitle.trim();
      let sectionDataAttr = '';

      if (sectionTrimmed === 'What went well this period:') {
        sectionDataAttr = 'what-went-well';
      } else if (
        sectionTrimmed === 'What can be improved for future periods:'
      ) {
        sectionDataAttr = 'what-can-be-improved';
      }

      return (
        <div
          key={`${sectionTitle}-${Math.random()}`}
          className="nexus-task-description-section"
          data-section={sectionDataAttr}
        >
          <div className="nexus-task-description-section-title">
            {sectionTitle}
          </div>
          <ul className="nexus-task-bullet-list">
            {sentences.map((sentence, index) => (
              <li
                key={`sentence-${index}`}
                className="nexus-task-bullet-item"
                data-number={index + 1}
              >
                {sentence.trim()}
                {sentence.trim().endsWith('.') ? '' : '.'}
              </li>
            ))}
          </ul>
        </div>
      );
    };

    // Function to process a section of text
    const processSection = (sectionTitle, sectionContent) => {
      // Special handling for sections that should be displayed as bullet lists
      if (
        sectionTitle.trim() === 'What went well this period:' ||
        sectionTitle.trim() === 'What can be improved for future periods:'
      ) {
        return renderAsBulletList(sectionTitle, sectionContent);
      }

      // Special handling for KPIs section
      if (sectionTitle.trim() === 'KPIs:') {
        // Parse the KPI content to extract revenue, expenses, and net income
        const revenueMatch = sectionContent.match(
          /Revenue:?\s*\$?(\d+(?:,\d+)*(?:\.\d+)?)\s*\(([-+]?\d+\.\d+)%\s*YOY,\s*([-+]?\d+\.\d+)%\s*MOM\)/i
        );
        const expensesMatch = sectionContent.match(
          /Expenses:?\s*\$?(\d+(?:,\d+)*(?:\.\d+)?)\s*\(([-+]?\d+\.\d+)%\s*YOY,\s*([-+]?\d+\.\d+)%\s*MOM\)/i
        );
        const netIncomeMatch = sectionContent.match(
          /Net Income:?\s*\$?(\d+(?:,\d+)*(?:\.\d+)?)\s*\(([-+]?\d+\.\d+)%\s*of revenue\)/i
        );

        return (
          <div
            key={`${sectionTitle}-${Math.random()}`}
            className="nexus-task-description-section"
          >
            <div className="nexus-task-description-section-content-no-bg">
              {revenueMatch && (
                <div className="nexus-kpi-item">
                  <div className="nexus-kpi-label">Revenue:</div>
                  <div className="nexus-kpi-value">
                    ${revenueMatch[1]} ({revenueMatch[2]}% YOY,{' '}
                    {revenueMatch[3]}% MOM)
                  </div>
                  <hr />
                </div>
              )}

              {expensesMatch && (
                <div className="nexus-kpi-item">
                  <div className="nexus-kpi-label">Expenses:</div>
                  <div className="nexus-kpi-value">
                    ${expensesMatch[1]} ({expensesMatch[2]}% YOY,{' '}
                    {expensesMatch[3]}% MOM)
                  </div>
                  <hr />
                </div>
              )}

              {netIncomeMatch && (
                <div className="nexus-kpi-item">
                  <div className="nexus-kpi-label">Net Income:</div>
                  <div className="nexus-kpi-value">
                    ${netIncomeMatch[1]} ({netIncomeMatch[2]}% of revenue)
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      }

      // Special handling for Task sections
      if (sectionTitle.startsWith('Task ')) {
        // Parse the task content to identify subsections
        const taskTitle = sectionContent.split('\n')[0].trim();

        // Parse the task content to identify all subsections
        const subsections = {};

        // Extract Data Points section
        const dataPointsMatch = sectionContent.match(
          /(Data Points:)([\s\S]*?)(?=(Solution:|Potential Impact:|Priority:|$))/i
        );
        if (dataPointsMatch) {
          subsections.dataPoints = {
            title: dataPointsMatch[1].trim(),
            content: dataPointsMatch[2].trim(),
          };
        }

        // Extract Solution section
        const solutionMatch = sectionContent.match(
          /(Solution:)([\s\S]*?)(?=(Data Points:|Potential Impact:|Priority:|$))/i
        );
        if (solutionMatch) {
          subsections.solution = {
            title: solutionMatch[1].trim(),
            content: solutionMatch[2].trim(),
          };
        }

        // Extract Potential Impact section
        const impactMatch = sectionContent.match(
          /(Potential Impact:)([\s\S]*?)(?=(Solution:|Data Points:|Priority:|$))/i
        );
        if (impactMatch) {
          subsections.impact = {
            title: impactMatch[1].trim(),
            content: impactMatch[2].trim(),
          };
        }

        // Extract Priority section
        const priorityMatch = sectionContent.match(
          /(Priority:\s*)(\w+)([\s\S]*?)(?=(Solution:|Data Points:|Potential Impact:|$))/i
        );
        if (priorityMatch) {
          subsections.priority = {
            title: priorityMatch[1].trim(),
            level: priorityMatch[2].trim(),
            content: priorityMatch[3].trim(),
          };
        }

        // Helper function to format solution content
        const renderSolution = content => {
          // Check if the solution content contains phrases like "customers have" which indicates a list
          if (
            content.includes('customers have') ||
            content.includes('Other customers have')
          ) {
            // Split by sentences that end with a colon (likely introducing a list)
            const parts = content.split(/(.*?:)/);
            if (parts.length > 1) {
              // The first part is the introduction
              const intro = parts[1];
              // The second part contains the list items
              let listContent = parts[2];

              // Split the list content by line breaks or periods followed by spaces
              const listItems = listContent
                .split(/\s*[•-]\s*|\n|\. (?=[A-Z])/)
                .filter(item => item.trim().length > 0)
                .map(item => item.trim())
                .map(item => (item.endsWith('.') ? item : item + '.'));

              return (
                <div className="nexus-solution-content">
                  <div className="nexus-solution-intro">{intro}</div>
                  <ul className="nexus-solution-list">
                    {listItems.map((item, index) => (
                      <li key={index} className="nexus-solution-item">
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            }
          }

          // Default rendering if no list is detected
          return <div className="nexus-solution-content">{content}</div>;
        };

        // Helper function to format priority content
        const renderPriority = (level, content) => {
          const variant =
            level.toLowerCase() === 'high'
              ? 'danger'
              : level.toLowerCase() === 'medium'
                ? 'warning'
                : 'info';

          return (
            <div className="nexus-priority-content">
              <NexusTag variant={variant} size="small">
                {level}
              </NexusTag>
              <span className="nexus-priority-details">{content}</span>
            </div>
          );
        };

        return (
          <div
            key={`${sectionTitle}-${Math.random()}`}
            className="nexus-task-description-section nexus-task-item-section"
          >
            <div className="nexus-task-description-section-title nexus-task-item-title">
              {sectionTitle}: {taskTitle}
            </div>
            <div className="nexus-task-description-section-content-no-bg">
              {subsections.dataPoints && (
                <div className="nexus-task-subsection nexus-data-points-subsection">
                  <div className="nexus-task-subsection-title">
                    {subsections.dataPoints.title}
                  </div>
                  <div className="nexus-data-points-content">
                    {subsections.dataPoints.content}
                  </div>
                </div>
              )}

              {subsections.solution && (
                <div className="nexus-task-subsection nexus-solution-subsection">
                  <div className="nexus-task-subsection-title">
                    {subsections.solution.title}
                  </div>
                  {renderSolution(subsections.solution.content)}
                </div>
              )}

              {subsections.impact && (
                <div className="nexus-task-subsection nexus-impact-subsection">
                  <div className="nexus-task-subsection-title">
                    {subsections.impact.title}
                  </div>
                  <div className="nexus-impact-content">
                    {subsections.impact.content}
                  </div>
                </div>
              )}

              {subsections.priority && (
                <div className="nexus-task-subsection nexus-priority-subsection">
                  <div className="nexus-task-subsection-title">
                    {subsections.priority.title}
                  </div>
                  {renderPriority(
                    subsections.priority.level,
                    subsections.priority.content
                  )}
                </div>
              )}
            </div>
          </div>
        );
      }

      // Default handling for other sections
      return (
        <div
          key={`${sectionTitle}-${Math.random()}`}
          className="nexus-task-description-section"
        >
          <div className="nexus-task-description-section-title">
            {sectionTitle}
          </div>
          <div className="nexus-task-description-section-content">
            {sectionContent.trim()}
          </div>
        </div>
      );
    };

    // Function to process regular text (not part of a known section)
    const processRegularText = text => {
      if (!text || !text.trim()) return null;
      return (
        <div
          key={`regular-${Math.random()}`}
          className="nexus-task-description-regular"
        >
          {text.trim()}
        </div>
      );
    };

    // Combine all sections we want to look for
    const allSections = isSummaryTask
      ? [...summaryTaskSections, ...taskSubsections]
      : isErrorTask
        ? errorTaskSections
        : [];

    // If we don't have any sections to look for, just return the description as is
    if (allSections.length === 0) {
      return <div>{description}</div>;
    }

    // Create a regex pattern to find all section markers
    const sectionMarkerPattern = new RegExp(
      `(${allSections.map(s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`,
      'g'
    );

    // Split the description by section markers, but keep the markers
    const parts = [];
    let lastIndex = 0;
    let match;

    // Reset the regex lastIndex
    sectionMarkerPattern.lastIndex = 0;

    // Find all section markers
    while ((match = sectionMarkerPattern.exec(description)) !== null) {
      // If there's text before this section marker, add it as regular text
      if (match.index > lastIndex) {
        const regularText = description.substring(lastIndex, match.index);
        parts.push({ type: 'regular', text: regularText });
      }

      // Add the section marker
      parts.push({ type: 'marker', text: match[0] });

      // Update lastIndex to the end of this match
      lastIndex = match.index + match[0].length;
    }

    // Add any remaining text after the last section marker
    if (lastIndex < description.length) {
      parts.push({ type: 'regular', text: description.substring(lastIndex) });
    }

    // Process the parts to combine section markers with their content
    const formattedParts = [];
    let currentSection = null;
    let currentContent = '';
    let currentTaskSection = null; // Track if we're inside a Task section

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];

      if (part.type === 'marker') {
        // Check if this is a task subsection marker
        const isTaskSubsection = taskSubsections.includes(part.text);
        const isTaskSection = part.text.startsWith('Task ');

        // If we were already processing a section, finish it
        if (currentSection) {
          // If this is a subsection and we're in a task section, handle differently
          if (isTaskSubsection && currentTaskSection) {
            // For task subsections, we need to add a separator before the subsection marker
            // to ensure our regex can properly extract it later
            currentContent += '\n' + part.text + '\n';
            // Skip setting currentSection so we stay in the current task section
            continue;
          } else {
            formattedParts.push(processSection(currentSection, currentContent));
            currentContent = '';
          }
        }

        // Start a new section
        currentSection = part.text;

        // Track if we're entering a Task section
        if (isTaskSection) {
          currentTaskSection = part.text;
        } else if (!isTaskSubsection) {
          // If we're not entering a task section or subsection, we're exiting any task section
          currentTaskSection = null;
        }
      } else if (part.type === 'regular') {
        // If we're in a section, add to its content
        if (currentSection) {
          currentContent += part.text;
        }
        // Otherwise, it's standalone regular text
        else {
          const regularTextElement = processRegularText(part.text);
          if (regularTextElement) {
            formattedParts.push(regularTextElement);
          }
        }
      }
    }

    // Don't forget to add the last section if there is one
    if (currentSection) {
      formattedParts.push(processSection(currentSection, currentContent));
    }

    return <>{formattedParts}</>;
  }, []);

  // Function to fetch users for the dropdown
  const fetchUsers = useCallback(() => {
    setIsLoadingUsers(true);

    fetchWithAuth('/api/get_users/', {
      method: 'GET',
    })
      .then(response => response.json())
      .then(data => {
        if (Array.isArray(data.data)) {
          const userOptions = data.data.map(user => ({
            label: user.first_name,
            value: user.user_id_id,
          }));
          setUsers(userOptions);
        }
        setIsLoadingUsers(false);
      })
      .catch(err => {
        console.error('Error fetching users:', err);
        setIsLoadingUsers(false);
      });
  }, []);

  // Effect to handle drawer closing animation
  useEffect(() => {
    let timer;
    if (!isDrawerOpen && selectedTask) {
      setIsClosing(true);
      // Use a shorter timeout than the drawer's built-in 300ms
      timer = setTimeout(() => {
        setIsClosing(false);
      }, 10); // Reduced to 10ms for faster cleanup
    }
    return () => clearTimeout(timer);
  }, [isDrawerOpen, selectedTask]);

  // Add custom CSS for the task drawer
  // useEffect(() => {
  //   // Add custom CSS for the task drawer if it doesn't exist
  //   if (!document.getElementById('task-drawer-custom-css')) {
  //     const style = document.createElement('style');
  //     style.id = 'task-drawer-custom-css';
  //     style.innerHTML = `
  //       .nexus-task-header {
  //         margin-bottom: 24px;
  //       }
  //       .nexus-task-header-title {
  //         margin-bottom: 16px;
  //       }
  //       .nexus-task-header-text {
  //         font-size: 18px;
  //         font-weight: 600;
  //         color: #37456c;
  //         margin-bottom: 8px;
  //       }
  //       .nexus-task-details-grid {
  //         display: grid;
  //         grid-gap: 16px;
  //       }
  //       .nexus-task-detail-row {
  //         display: flex;
  //         justify-content: space-between;
  //         align-items: center;
  //       }
  //       .nexus-task-detail-label {
  //         display: flex;
  //         align-items: center;
  //         gap: 8px;
  //         color: #6E7191;
  //         font-size: 14px;
  //       }
  //       .nexus-task-detail-icon {
  //         width: 16px;
  //         height: 16px;
  //       }
  //       .nexus-task-status-value {
  //         display: flex;
  //         align-items: center;
  //         gap: 8px;
  //       }
  //       .nexus-task-status-value-image {
  //         width: 20px;
  //         height: 20px;
  //       }
  //       .nexus-task-assigned-display, .nexus-task-due-display {
  //         display: flex;
  //         align-items: center;
  //         gap: 8px;
  //       }
  //       .nexus-task-description {
  //         margin-top: 24px;
  //       }
  //       .nexus-task-bullet-list {
  //         list-style-type: none;
  //         padding-left: 0;
  //       }
  //       .nexus-task-bullet-item {
  //         position: relative;
  //         padding-left: 28px;
  //         margin-bottom: 8px;
  //       }
  //       .nexus-task-bullet-item::before {
  //         content: attr(data-number);
  //         position: absolute;
  //         left: 0;
  //         top: 0;
  //         width: 20px;
  //         height: 20px;
  //         background-color: #2D71E8;
  //         color: white;
  //         border-radius: 50%;
  //         display: flex;
  //         align-items: center;
  //         justify-content: center;
  //         font-size: 12px;
  //         font-weight: 600;
  //       }
  //       .nexus-task-description-section {
  //         margin-bottom: 24px;
  //       }
  //       .nexus-task-description-section-title {
  //         font-weight: 600;
  //         margin-bottom: 8px;
  //         color: #37456c;
  //       }
  //       .nexus-task-description-section-content {
  //         background-color: #f8f9fc;
  //         padding: 12px;
  //         border-radius: 4px;
  //       }
  //       .nexus-task-description-section-content-no-bg {
  //         padding: 4px 0;
  //       }
  //       .nexus-task-item-section {
  //         border: 1px solid #e0e0e0;
  //         border-radius: 4px;
  //         padding: 16px;
  //         margin-bottom: 16px;
  //       }
  //       .nexus-task-item-title {
  //         font-size: 16px;
  //         margin-bottom: 16px;
  //       }
  //       .nexus-task-subsection {
  //         margin-bottom: 16px;
  //       }
  //       .nexus-task-subsection-title {
  //         font-weight: 600;
  //         margin-bottom: 8px;
  //         color: #6E7191;
  //       }
  //       .nexus-data-points-content, .nexus-impact-content {
  //         line-height: 1.5;
  //       }
  //       .nexus-solution-content {
  //         line-height: 1.5;
  //       }
  //       .nexus-solution-intro {
  //         margin-bottom: 8px;
  //       }
  //       .nexus-solution-list {
  //         padding-left: 20px;
  //       }
  //       .nexus-solution-item {
  //         margin-bottom: 4px;
  //       }
  //       .nexus-priority-content {
  //         display: flex;
  //         align-items: center;
  //         gap: 8px;
  //       }
  //       .nexus-priority-details {
  //         line-height: 1.5;
  //       }
  //       .nexus-kpi-item {
  //         display: flex;
  //         flex-direction: column;
  //         margin-bottom: 16px;
  //         padding: 0 0 16px 0;
  //         border-bottom: 1px solid #e0e0e0;
  //       }
  //       .nexus-kpi-item:last-child {
  //         border-bottom: none;
  //       }
  //       .nexus-kpi-label {
  //         font-weight: 600;
  //         color: #37456c;
  //         font-size: 16px;
  //         margin-bottom: 8px;
  //       }
  //       .nexus-kpi-value {
  //         color: #37456c;
  //         font-weight: 500;
  //         font-size: 15px;
  //       }
  //       .nexus-task-drawer-buttons {
  //         display: flex;
  //         gap: 16px;
  //         justify-content: flex-end;
  //       }
  //       .nexus-task-drawer-header {
  //         font-weight: 600;
  //         font-size: 18px;
  //       }
  //       .nexus-task-drawer-body {
  //         padding: 24px;
  //       }
  //       .nexus-task-drawer-content {
  //         max-width: 100%;
  //       }
  //     `;
  //     document.head.appendChild(style);
  //   }
  // }, []);

  // Effect to fetch users when drawer opens
  useEffect(() => {
    if (isDrawerOpen && selectedTask) {
      // Initialize the local task state with the selected task
      setLocalTask({ ...selectedTask });

      // Only fetch users if not from task table (since editing is disabled)
      if (!isFromTaskTable) {
        fetchUsers();
      }

      // Find the user ID from the task data
      // The API might return the assigned user ID in different ways
      const userId =
        selectedTask.assigned_to_id ||
        selectedTask.user_id_id ||
        selectedTask.assignedToId;

      // Store the user ID in state
      setSelectedUser(userId);

      // Reset editing states when drawer opens
      setIsEditingAssignment(false);
      setIsEditingDueDate(false);

      // Parse the due date if it exists
      if (selectedTask.dueDate) {
        try {
          // Create a date object from the date string
          const dateParts = selectedTask.dueDate.split('-');
          // Note: months are 0-indexed in JavaScript Date
          const dateObj = new Date(
            dateParts[0],
            dateParts[1] - 1,
            dateParts[2]
          );
          setSelectedDate(dateObj);
        } catch (error) {
          console.error('Error parsing due date:', error);
          setSelectedDate(null);
        }
      } else {
        setSelectedDate(null);
      }
    }
  }, [isDrawerOpen, selectedTask, fetchUsers, isFromTaskTable]);

  // Effect to handle clicks outside the assignment editor
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        assignmentRef.current &&
        !assignmentRef.current.contains(event.target)
      ) {
        setIsEditingAssignment(false);
      }
    };

    if (isEditingAssignment) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditingAssignment]);

  // Effect to handle clicks outside the due date editor
  useEffect(() => {
    const handleClickOutside = event => {
      // Don't close if clicking on the rsuite picker dropdown
      const isRsuitePicker = !!event.target.closest('.rs-picker-menu');
      if (
        dueDateRef.current &&
        !dueDateRef.current.contains(event.target) &&
        !isRsuitePicker
      ) {
        setIsEditingDueDate(false);
      }
    };

    let timeoutId;
    if (isEditingDueDate) {
      // Use a slight delay to allow the date selection to be processed
      timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      // Clear the timeout to prevent memory leaks
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditingDueDate]);

  // Function to make a complete task update with all fields
  const updateTaskWithAllFields = useCallback(
    (updatedFields = {}) => {
      if (!selectedTask) return Promise.reject(new Error('No task selected'));

      // Get the current assigned user ID - prioritize the one in state
      const assignedUserId =
        updatedFields.assigned_to ||
        selectedUser ||
        selectedTask.assigned_to_id ||
        selectedTask.user_id_id ||
        selectedTask.assignedToId ||
        '';

      console.log(
        'Updating task with all fields, assigned user ID:',
        assignedUserId
      );

      const taskData = new FormData();

      // Always include all fields to ensure nothing is lost
      taskData.append('taskId', selectedTask.id);
      taskData.append('action', 'modify');
      taskData.append(
        'task_title',
        updatedFields.task_title || selectedTask.header || ''
      );
      taskData.append(
        'task_desc',
        updatedFields.task_desc || selectedTask.description || ''
      );
      taskData.append(
        'due_on',
        updatedFields.due_on || selectedTask.dueDate || ''
      );

      // CRITICAL: Always include the assigned_to field with a valid value
      if (assignedUserId) {
        taskData.append('assigned_to', assignedUserId);
      } else {
        // If we still don't have a valid ID, log a warning
        console.warn(
          'No assigned user ID found for task in updateTaskWithAllFields:',
          selectedTask.id
        );

        // As a fallback, if the task has a firstName but no ID, try to find the user ID
        if (selectedTask.firstName && users.length > 0) {
          const userMatch = users.find(
            user => user.label === selectedTask.firstName
          );
          if (userMatch) {
            console.log('Found user ID from firstName:', userMatch.value);
            taskData.append('assigned_to', userMatch.value);
          } else {
            // If we can't find a match, use the current user as a last resort
            const currentUserId = localStorage.getItem('user_id');
            if (currentUserId) {
              console.log('Using current user ID as fallback:', currentUserId);
              taskData.append('assigned_to', currentUserId);
            } else {
              // If all else fails, send an empty string (though this will likely clear the assignment)
              taskData.append('assigned_to', '');
            }
          }
        } else {
          // If all else fails, send an empty string (though this will likely clear the assignment)
          taskData.append('assigned_to', '');
        }
      }

      taskData.append(
        'status',
        updatedFields.status || selectedTask.status || 'Not yet Started'
      );

      return fetchWithAuth('/api/modify_task/', {
        method: 'POST',
        body: taskData,
      })
        .then(response => response.json())
        .then(data => {
          fetchTasks();
          return data;
        })
        .catch(err => {
          console.error('Error updating task:', err);
          throw err;
        });
    },
    [selectedTask, selectedUser, fetchTasks, users]
  );

  // Function to handle user assignment change
  const handleAssignUser = useCallback(
    (_, userId) => {
      if (!selectedTask || userId === selectedTask.user_id_id) return;

      // Update the selected user in state
      setSelectedUser(userId);

      // Find the user name from the users array
      const selectedUserObj = users.find(user => user.value === userId);
      const firstName = selectedUserObj ? selectedUserObj.label : '';

      // Update the local task with the new assignment
      setLocalTask(prevTask => ({
        ...prevTask,
        user_id_id: userId,
        firstName: firstName,
      }));

      // Use the comprehensive update function
      updateTaskWithAllFields({ assigned_to: userId })
        .then(() => {
          // Show success toast notification using Nexus Toast
          showToast({
            priority: 'success', // Valid values are 'success' or 'error'
            content: 'Task has been successfully reassigned',
            autoClose: 6000,
          });

          // Exit edit mode after successful assignment
          setIsEditingAssignment(false);
        })
        .catch(() => {
          // Show error toast
          showToast({
            priority: 'error', // Changed from 'danger' to 'error' - valid values are 'success' or 'error'
            content: 'Failed to update task assignment',
            autoClose: 6000,
          });
        });
    },
    [selectedTask, updateTaskWithAllFields, users]
  );

  // Function to handle due date change
  const handleDueDateChange = useCallback(
    date => {
      if (!selectedTask || !date) {
        return;
      }

      // Make sure date is a valid Date object
      if (!(date instanceof Date) || isNaN(date.getTime())) {
        return;
      }

      // Format the date as YYYY-MM-DD
      // rsuite DatePicker returns a Date object
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
      const day = String(date.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;

      // Store the selected date in state
      setSelectedDate(date);

      // Update the local task with the new due date
      setLocalTask(prevTask => ({
        ...prevTask,
        dueDate: formattedDate,
      }));

      // Get the current assigned user ID - prioritize the one in state
      // Check all possible field names where the assigned user ID might be stored
      const assignedUserId =
        selectedUser ||
        selectedTask.assigned_to_id ||
        selectedTask.user_id_id ||
        selectedTask.assignedToId ||
        '';

      console.log(
        'Updating task due date with assigned user ID:',
        assignedUserId
      );

      // Create a direct FormData object instead of using updateTaskWithAllFields
      const taskData = new FormData();

      // IMPORTANT: Always include all required fields
      taskData.append('taskId', selectedTask.id);
      taskData.append('action', 'modify');
      taskData.append('task_title', selectedTask.header || '');
      taskData.append('task_desc', selectedTask.description || '');
      taskData.append('due_on', formattedDate);

      // CRITICAL: Always include the assigned_to field with a valid value
      if (assignedUserId) {
        taskData.append('assigned_to', assignedUserId);
      } else {
        // If we still don't have a valid ID, log a warning
        console.warn(
          'No assigned user ID found for task when updating due date:',
          selectedTask.id
        );

        // As a fallback, if the task has a firstName but no ID, try to find the user ID
        if (selectedTask.firstName && users.length > 0) {
          const userMatch = users.find(
            user => user.label === selectedTask.firstName
          );
          if (userMatch) {
            console.log('Found user ID from firstName:', userMatch.value);
            taskData.append('assigned_to', userMatch.value);
          } else {
            // If we can't find a match, use the current user as a last resort
            const currentUserId = localStorage.getItem('user_id');
            if (currentUserId) {
              console.log('Using current user ID as fallback:', currentUserId);
              taskData.append('assigned_to', currentUserId);
            } else {
              // If all else fails, send an empty string (though this will likely clear the assignment)
              taskData.append('assigned_to', '');
            }
          }
        } else {
          // If all else fails, send an empty string (though this will likely clear the assignment)
          taskData.append('assigned_to', '');
        }
      }

      taskData.append('status', selectedTask.status || 'Not yet Started');

      // Make the API call directly
      fetchWithAuth('/api/modify_task/', {
        method: 'POST',
        body: taskData,
      })
        .then(response => response.json())
        .then(data => {
          fetchTasks();

          // Show success toast notification using Nexus Toast
          showToast({
            priority: 'success', // Valid values are 'success' or 'error'
            content: 'Task due date has been successfully updated',
            autoClose: 6000,
          });

          // Exit edit mode after successful update
          setIsEditingDueDate(false);
        })
        .catch(err => {
          console.error('Error updating task due date:', err);
          // Show error toast
          showToast({
            priority: 'error', // Changed from 'danger' to 'error' - valid values are 'success' or 'error'
            content: 'Failed to update task due date',
            autoClose: 6000,
          });
        });
    },
    [selectedTask, selectedUser, fetchTasks, users]
  );

  // Memoize the drawer content
  const drawerContent = useMemo(() => {
    if (!selectedTask) return null;

    const assignedToUserId =
      selectedUser || localTask?.assignedToId || selectedTask?.assignedToId;

    const assignedToUser = users.find(user => user.value === assignedToUserId);

    return (
      <>
        <div className="nexus-task-header">
          <div className="nexus-task-header-title">
            <div className="nexus-task-header-text">
              {selectedTask.header || ''}
            </div>
          </div>

          {/* Task details grid based on the image */}
          <div className="nexus-task-details-grid">
            {/* Status row */}
            <div className="nexus-task-detail-row">
              <div className="nexus-task-detail-label">
                <img
                  src={statusSvg}
                  alt="Status"
                  className="nexus-task-detail-icon"
                />
                <span className="nexus-task-detail-text">Status</span>
              </div>
              <div className="nexus-task-status-value">
                <img
                  src={getStatusImage(
                    localTask?.status ||
                      selectedTask.status ||
                      'Not yet Started'
                  )}
                  alt={
                    localTask?.status ||
                    selectedTask.status ||
                    'Not yet Started'
                  }
                  className="nexus-task-status-value-image"
                />
                <span className="nexus-task-status-value-text">
                  {localTask?.status ||
                    selectedTask.status ||
                    'Not yet Started'}
                </span>
              </div>
            </div>

            {/* Priority row */}
            <div className="nexus-task-detail-row">
              <div className="nexus-task-detail-label">
                <ChevronUpCircleIcon size="16px" color="#6E7191" />
                <span className="nexus-task-detail-text">Priority</span>
              </div>
              <div>
                <NexusTag
                  variant={getPriorityVariant(
                    extractPriorityFromDescription(selectedTask.description)
                  )}
                  size="small"
                >
                  {extractPriorityFromDescription(selectedTask.description)}
                </NexusTag>
              </div>
            </div>

            {/* Assigned to row */}
            <div className="nexus-task-detail-row">
              <div className="nexus-task-detail-label">
                <UsersIcon size="16px" color="#6E7191" />
                <span className="nexus-task-detail-text">Assigned to</span>
              </div>
              <div className="nexus-task-assigned-selector" ref={assignmentRef}>
                {isEditingAssignment ? (
                  <SelectMenu
                    options={users}
                    value={selectedUser}
                    onChange={handleAssignUser}
                    placeholder={
                      isLoadingUsers ? 'Loading users...' : 'Select user'
                    }
                    style={{
                      width: '100%',
                      minWidth: '120px',
                      fontSize: '0.875rem',
                      fontWeight: '400',
                    }}
                    disabled={isLoadingUsers}
                    autoFocus
                  />
                ) : (
                  <div className="nexus-task-assigned-display">
                    <span className="nexus-task-assigned-value">
                      {assignedToUser?.label || ''}
                    </span>
                    {!isFromTaskTable && (
                      <IconButton
                        variant="ghost"
                        size="small"
                        className="nexus-edit-assignment-button"
                        onClick={() =>
                          !isFromTaskTable && setIsEditingAssignment(true)
                        }
                        icon={() => <EditIcon size="14px" color="#2D71E8" />}
                        aria-label="Edit assignment"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Due date row */}
            <div className="nexus-task-detail-row">
              <div className="nexus-task-detail-label">
                <CalendarIcon size="16px" color="#6E7191" />
                <span className="nexus-task-detail-text">Due</span>
              </div>
              <div className="nexus-task-due-selector" ref={dueDateRef}>
                {isEditingDueDate ? (
                  <DatePicker
                    size="sm"
                    appearance="default"
                    placeholder="Select date"
                    style={{
                      width: '100%',
                      minWidth: '120px',
                      fontSize: '0.875rem',
                      fontWeight: '400',
                    }}
                    placement="bottomEnd"
                    value={selectedDate ? new Date(selectedDate) : null}
                    onChange={date => {
                      if (date) {
                        handleDueDateChange(date);
                      }
                    }}
                    format="yyyy-MM-dd"
                    oneTap
                    block
                    caretAs={() => <CalendarIcon size="16px" color="#6E7191" />}
                    clearable={false}
                  />
                ) : (
                  <div className="nexus-task-due-display">
                    <span className="nexus-task-due-value">
                      {localTask?.dueDate || selectedTask.dueDate || ''}
                    </span>
                    {!isFromTaskTable && (
                      <IconButton
                        variant="ghost"
                        size="small"
                        className="nexus-edit-due-button"
                        onClick={() =>
                          !isFromTaskTable && setIsEditingDueDate(true)
                        }
                        icon={() => <EditIcon size="14px" color="#2D71E8" />}
                        aria-label="Edit due date"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="nexus-task-description">
          <div className="nexus-task-description-content">
            {formatTaskDescription(
              selectedTask.description,
              selectedTask.header
            )}
          </div>
        </div>
      </>
    );
  }, [
    selectedTask,
    localTask,
    formatTaskDescription,
    users,
    selectedUser,
    handleAssignUser,
    isLoadingUsers,
    isEditingAssignment,
    isEditingDueDate,
    selectedDate,
    handleDueDateChange,
  ]);

  // Function to directly update task status
  const updateTaskStatus = useCallback(
    status => {
      if (!selectedTask) return Promise.reject(new Error('No task selected'));

      // Update the local task with the new status
      setLocalTask(prevTask => ({
        ...prevTask,
        status: status,
      }));

      // Get the current assigned user ID - prioritize the one in state
      // Check all possible field names where the assigned user ID might be stored
      const assignedUserId =
        selectedUser ||
        selectedTask.assigned_to_id ||
        selectedTask.user_id_id ||
        selectedTask.assignedToId ||
        '';

      console.log(
        'Updating task status with assigned user ID:',
        assignedUserId
      );
      console.log('Selected task data:', selectedTask);

      const taskData = new FormData();

      // IMPORTANT: Always include all required fields, even when just updating status
      taskData.append('taskId', selectedTask.id);
      taskData.append('action', 'modify'); // Use 'modify' instead of 'status_update' to ensure all fields are updated
      taskData.append('task_title', selectedTask.header || '');
      taskData.append('task_desc', selectedTask.description || '');
      taskData.append('due_on', selectedTask.dueDate || '');

      // CRITICAL: Always include the assigned_to field with a valid value
      // If we don't have a valid assignedUserId, try to get it from the API response
      if (assignedUserId) {
        taskData.append('assigned_to', assignedUserId);
      } else {
        // If we still don't have a valid ID, log a warning
        console.warn('No assigned user ID found for task:', selectedTask.id);

        // As a fallback, if the task has a firstName but no ID, try to find the user ID
        if (selectedTask.firstName && users.length > 0) {
          const userMatch = users.find(
            user => user.label === selectedTask.firstName
          );
          if (userMatch) {
            console.log('Found user ID from firstName:', userMatch.value);
            taskData.append('assigned_to', userMatch.value);
          } else {
            // If we can't find a match, use the current user as a last resort
            // This is better than losing the assignment completely
            const currentUserId = localStorage.getItem('user_id');
            if (currentUserId) {
              console.log('Using current user ID as fallback:', currentUserId);
              taskData.append('assigned_to', currentUserId);
            } else {
              // If all else fails, send an empty string (though this will likely clear the assignment)
              taskData.append('assigned_to', '');
            }
          }
        } else {
          // If all else fails, send an empty string (though this will likely clear the assignment)
          taskData.append('assigned_to', '');
        }
      }

      taskData.append('status', status);

      return fetchWithAuth('/api/modify_task/', {
        method: 'POST',
        body: taskData,
      })
        .then(response => response.json())
        .then(data => {
          fetchTasks();
          return data;
        })
        .catch(err => {
          console.error(`Error updating task status to ${status}:`, err);
          throw err;
        });
    },
    [selectedTask, selectedUser, fetchTasks, users]
  );

  // Memoize the drawer footer buttons
  const drawerFooterButtons = useMemo(() => {
    if (!selectedTask) return <div className="nexus-task-drawer-buttons"></div>;

    const handleCancelTask = () => {
      // Use the direct status update function
      updateTaskStatus('Cancelled')
        .then(() => {
          onClose();
        })
        .catch(() => {
          showToast({
            priority: 'error', // Changed from 'danger' to 'error' - valid values are 'success' or 'error'
            content: 'Failed to cancel task',
            autoClose: 6000,
          });
        });
    };

    const handleInProgressTask = () => {
      // Use the direct status update function
      updateTaskStatus('In Progress')
        .then(() => {
          onClose();
        })
        .catch(() => {
          showToast({
            priority: 'error', // Changed from 'danger' to 'error' - valid values are 'success' or 'error'
            content: 'Failed to update task status',
            autoClose: 6000,
          });
        });
    };

    const handleCompleteTask = () => {
      // Use the direct status update function
      updateTaskStatus('Completed')
        .then(() => {
          onClose();
        })
        .catch(() => {
          showToast({
            priority: 'error', // Changed from 'danger' to 'error' - valid values are 'success' or 'error'
            content: 'Failed to complete task',
            autoClose: 6000,
          });
        });
    };

    return (
      <div className="nexus-task-drawer-buttons">
        {selectedTask.status === 'In Progress' ? (
          <Button
            variant="tonal"
            className="nexus-cancel-text-button"
            onClick={handleCancelTask}
          >
            Cancel
          </Button>
        ) : (
          <IconButton
            variant="ghost"
            className="nexus-cancel-button"
            icon={() => (
              <img
                src={deleteIconSvg}
                alt="Cancel Task"
                width="20"
                height="20"
              />
            )}
            onClick={handleCancelTask}
          />
        )}
        {selectedTask.status !== 'In Progress' && (
          <IconButton
            variant="ghost"
            className="nexus-in-progress-button"
            icon={() => (
              <img
                src={listNumberIconSvg}
                alt="In Progress"
                width="20"
                height="20"
              />
            )}
            onClick={handleInProgressTask}
          />
        )}
        <Button
          variant="filled"
          className="nexus-complete-button"
          onClick={handleCompleteTask}
        >
          Complete
        </Button>
      </div>
    );
  }, [selectedTask, onClose, updateTaskStatus]);

  return (
    <>
      {(selectedTask || isClosing) && (
        <Drawer
          isOpen={isDrawerOpen}
          onClose={onClose}
          className="nexus-task-drawer"
          data-testid="nexus-task-drawer"
        >
          <DrawerHeader onClose={onClose} className="nexus-task-drawer-header">
            Task details
          </DrawerHeader>
          <DrawerBody className="nexus-task-drawer-body">
            <div className="nexus-task-drawer-content">{drawerContent}</div>
          </DrawerBody>
          {!isFromTaskTable && (
            <DrawerFooter className="nexus-task-drawer-footer">
              {drawerFooterButtons}
            </DrawerFooter>
          )}
        </Drawer>
      )}
    </>
  );
};

export default TaskDrawer;
